// =============================================================================
// Plugin Auth Page
// OAuth2 callback for the Darkfeld browser extension
//
// Three rendering contexts:
//
//   1. IFRAME  (window.parent !== window)
//      Embedded in the VTT page by overlay.ts. If the user is already logged
//      in the token is posted directly to window.parent. If not, a popup is
//      opened to run the full OAuth flow (Discord / Cognito block iframes).
//      The popup relays the token back via BroadcastChannel.
//
//   2. POPUP   (URL contains ?popup=1)
//      Opened by the iframe path above. Runs the full OAuth redirect, then
//      on return broadcasts the access token and closes itself.
//
//   3. DIRECT  (no parent, no popup flag)
//      Standalone navigation — shows an error.
// =============================================================================
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { initializeAuth, loginWithDiscord } from '@/store/slices/authSlice';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Shield } from 'lucide-react';
import {
  isSymbioteTokenMessage, PLUGIN_POPUP_PENDING_KEY, SYMBIOTE_POPUP_PENDING_KEY,
} from '@/lib/authHandoff';


// Read the extension ID injected by overlay.ts into the iframe URL hash.
// Format: /auth/plugin#extId=<chrome-extension-id>
function getExtIdFromHash(): string {
  return new URLSearchParams(window.location.hash.slice(1)).get('extId') ?? '';
}

export default function PluginAuthPage() {
  const dispatch = useAppDispatch();
  const { user, isLoading } = useAppSelector((s) => s.auth);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPopupMode = searchParams.get('popup') === '1';
  const isSymbioteMode = searchParams.get('symbiote') === '1';
  const symbioteNonce = searchParams.get('nonce') ?? '';
  // extId: in the hash when we're the iframe (put there by overlay.ts),
  // or in the query string when we're the popup (forwarded by handleDiscordLogin)
  const extId = isPopupMode
    ? (searchParams.get('extId') ?? '')
    : getExtIdFromHash();
  const [status, setStatus] = useState<'checking' | 'needs_login' | 'authenticating' | 'posting' | 'done'>('checking');
  const [error, setError] = useState<string | null>(null);

  // On mount, ensure auth state is hydrated
  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  // TaleSpire's loadTargetBehavior="popup" opens Cognito/Discord in a second,
  // nested popup instead of navigating this original authorization popup. The
  // OAuth callback therefore posts its token here first. Relay the validated
  // message to the original Symbiote window, which owns TaleSpire storage.
  useEffect(() => {
    if (!isSymbioteMode || !symbioteNonce) return;

    const relaySymbioteToken = (event: MessageEvent) => {
      if (!isSymbioteTokenMessage(event.origin, window.location.origin, event.data, symbioteNonce)) return;
      if (!window.opener) {
        setError('The TaleSpire authorization request is no longer active.');
        setStatus('needs_login');
        return;
      }
      window.opener.postMessage(event.data, window.location.origin);
      setStatus('done');
      setTimeout(() => window.close(), 300);
    };

    window.addEventListener('message', relaySymbioteToken);
    return () => window.removeEventListener('message', relaySymbioteToken);
  }, [isSymbioteMode, symbioteNonce]);

  // When auth state changes, attempt to post an access token to the plugin
  useEffect(() => {
    if (isLoading) {
      setStatus('checking');
      return;
    }

    if (user) {
      // User is logged in — get access token and send to plugin
      sendTokenToPlugin();
    } else {
      // User not logged in — show login button
      setStatus('needs_login');
    }
  }, [user, isLoading]);

  /**
   * Extract access token from Amplify session and deliver it to the extension.
   *
   * - IFRAME: post to window.parent → caught by overlay.ts in the VTT page
   *           (user was already logged in; no popup needed).
   * - POPUP:  send directly to the extension background via
   *           chrome.runtime.sendMessage (externally_connectable). This
   *           bypasses all cross-origin storage partitioning. When done,
   *           close the popup; the iframe detects closure and tells
   *           overlay.ts to verify auth state and close the dialog.
   */
  async function sendTokenToPlugin() {
    setStatus('posting');
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        setError('Failed to get authentication token. Please try again.');
        setStatus('needs_login');
        return;
      }

      if (window.parent !== window) {
        // IFRAME — already logged in; relay token to overlay.ts then hand
        // off to the compact combat executor so the dialog becomes useful.
        window.parent.postMessage(
          { type: 'DARKFELD_PLUGIN_TOKEN', token: accessToken, idToken: accessToken },
          '*'
        );
        navigate('/plugin/combat', { replace: true });
      } else if (isSymbioteMode) {
        if (!window.opener || !symbioteNonce) {
          setError('The TaleSpire authorization request is missing or no longer active.');
          setStatus('needs_login');
          return;
        }
        window.opener.postMessage(
          { type: 'DARKFELD_SYMBIOTE_TOKEN', token: accessToken, nonce: symbioteNonce },
          window.location.origin
        );
        setStatus('done');
        setTimeout(() => window.close(), 300);
      } else if (isPopupMode) {
        // POPUP — deliver token to the extension background.
        //
        // Two parallel paths; first success wins:
        //
        //   Path A (Chrome): chrome.runtime.sendMessage(extId, ...) via
        //     externally_connectable — direct, no content script needed.
        //
        //   Path B (Firefox + Chrome fallback): window.postMessage →
        //     content-darkfeld.ts content script → chrome.runtime.sendMessage.
        //     Firefox does not support externally_connectable so this is the
        //     only path there. Chrome uses Path A but Path B fires harmlessly.
        let settled = false;

        function onSuccess() {
          if (settled) return;
          settled = true;
          setStatus('done');
          setTimeout(() => window.close(), 300);
        }

        function onFailure(msg: string) {
          if (settled) return;
          settled = true;
          setError(`Auth failed: ${msg}`);
          setStatus('needs_login');
        }

        // Path B — relay via content script (Firefox + Chrome fallback)
        const resultHandler = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          if (event.data?.type !== 'DARKFELD_SAVE_TOKEN_RESULT') return;
          window.removeEventListener('message', resultHandler);
          if (event.data.success) onSuccess();
          else onFailure(event.data.error ?? 'unknown');
        };
        window.addEventListener('message', resultHandler);
        window.postMessage(
          { type: 'DARKFELD_SAVE_TOKEN_RELAY', token: accessToken },
          window.location.origin
        );

        // Path A — externally_connectable (Chrome only)
        const cr = (window as any).chrome?.runtime;
        if (extId && cr) {
          cr.sendMessage(extId, { type: 'SAVE_TOKEN', token: accessToken }, (res: any) => {
            if (!cr.lastError && res?.success) onSuccess();
            // If Path A fails, Path B result handler may still fire — don't error yet
          });
        }

        // If neither path responds within 10 s, show an error
        setTimeout(() => {
          window.removeEventListener('message', resultHandler);
          if (!settled) onFailure('No response from extension. Is it installed and enabled?');
        }, 10_000);
      } else {
        setError('This page must be opened by the Darkfeld extension.');
        setStatus('needs_login');
      }
    } catch (err) {
      setError(`Authentication error: ${String(err)}`);
      setStatus('needs_login');
    }
  }

  const handleDiscordLogin = () => {
    if (window.parent !== window) {
      // IFRAME — open popup for OAuth (Discord/Cognito block iframes).
      // Pass extId so the popup can call chrome.runtime.sendMessage.
      // When the popup closes, poll to detect it and tell overlay.ts to
      // verify auth state and close the dialog.
      setStatus('authenticating');
      localStorage.setItem(PLUGIN_POPUP_PENDING_KEY, '1');
      const popup = window.open(
        `/auth/plugin?popup=1&extId=${encodeURIComponent(extId)}`,
        'darkfeld-auth',
        'popup=yes,width=480,height=680,scrollbars=yes'
      );
      if (!popup) {
        localStorage.removeItem(PLUGIN_POPUP_PENDING_KEY);
        setError('Popup was blocked. Please allow popups for this site and try again.');
        setStatus('needs_login');
        return;
      }
      // Poll for popup closure, then signal overlay.ts to check auth state
      const timer = setInterval(() => {
        if (popup.closed) {
          clearInterval(timer);
          window.parent.postMessage({ type: 'CHECK_AUTH' }, '*');
        }
      }, 300);
    } else if (isSymbioteMode) {
      setStatus('authenticating');
      localStorage.setItem(SYMBIOTE_POPUP_PENDING_KEY, JSON.stringify({ nonce: symbioteNonce }));
      dispatch(loginWithDiscord());
    } else {
      // POPUP — OAuth redirect works normally here
      setStatus('authenticating');
      dispatch(loginWithDiscord());
    }
  };

  return (
    <div className="min-h-screen bg-darkbg flex flex-col items-center justify-center px-4 relative overflow-hidden">
      {/* Ambient glows */}
      <div className="absolute inset-0 bg-gold-radial pointer-events-none" />

      <div className="relative w-full max-w-sm space-y-6 text-center animate-fade-in">
        {/* Logo */}
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-gold/10 blur-2xl scale-[2]" />
            <div
              className="relative w-16 h-16 rounded-full
                          bg-gradient-to-b from-darkbg-card to-darkbg-tertiary
                          border border-gold/30 shadow-glow-sm
                          flex items-center justify-center"
            >
              <Shield className="w-8 h-8 text-gold drop-shadow-[0_0_8px_rgba(201,168,76,0.7)]" />
            </div>
          </div>
          <h1 className="font-display text-3xl tracking-widest text-gold drop-shadow-[0_0_20px_rgba(201,168,76,0.35)]">
            DARKFELD
          </h1>
        </div>

        {/* Status messages */}
        {status === 'checking' && (
          <div className="space-y-3">
            <p className="text-parchment-muted text-sm">Checking authentication…</p>
            <div className="flex justify-center">
              <div className="w-6 h-6 rounded-full border-2 border-gold/20 border-t-gold animate-spin" />
            </div>
          </div>
        )}

        {status === 'needs_login' && (
          <div className="space-y-4">
            <div>
              <p className="text-parchment font-display text-lg">
                {isSymbioteMode ? 'Connect Darkfeld to TaleSpire' : 'Connect Darkfeld Extension'}
              </p>
              <p className="text-parchment-muted/70 text-sm mt-2">
                Sign in with your Discord account to authorize {isSymbioteMode ? 'the TaleSpire Symbiote' : 'the plugin'}
              </p>
            </div>

            {error && (
              <div className="bg-red-900/40 border border-red-700/60 rounded-lg px-4 py-3">
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            <button
              onClick={handleDiscordLogin}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg
                         bg-[#5865F2] hover:bg-[#4752C4] active:bg-[#3c45a5]
                         disabled:opacity-50 disabled:cursor-not-allowed
                         text-white font-semibold text-base
                         shadow-lg hover:shadow-[0_4px_20px_rgba(88,101,242,0.4)]
                         transition-all duration-150"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.042.028.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
              </svg>
              Sign in with Discord
            </button>
          </div>
        )}

        {status === 'authenticating' && (
          <div className="space-y-3">
            <p className="text-parchment-muted text-sm">Signing in with Discord…</p>
            <div className="flex justify-center">
              <div className="w-6 h-6 rounded-full border-2 border-gold/20 border-t-gold animate-spin" />
            </div>
          </div>
        )}

        {status === 'posting' && (
          <div className="space-y-3">
            <p className="text-parchment-muted text-sm">Authorizing extension…</p>
            <div className="flex justify-center">
              <div className="w-6 h-6 rounded-full border-2 border-gold/20 border-t-gold animate-spin" />
            </div>
          </div>
        )}

        {status === 'done' && (
          <div className="space-y-3">
            <p className="text-parchment text-sm">✓ Authorization successful</p>
            <p className="text-parchment-muted/70 text-xs">
              Your {isSymbioteMode ? 'TaleSpire Symbiote' : 'extension'} is now connected
            </p>
          </div>
        )}

        <p className="text-parchment-muted/30 text-xs">
          Secure OAuth2 authentication via Discord
        </p>
      </div>
    </div>
  );
}
